import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import { Modal } from '../../ui/Modal';
import { Input } from '../../ui/Input';
import { Button } from '../../ui/Button';

interface CreateProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateProjectModal: React.FC<CreateProjectModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { currentOrg, createProject } = useAuth();
  const { showToast } = useToast();

  const [newProjName, setNewProjName] = useState('');
  const [isCreatingProj, setIsCreatingProj] = useState(false);

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProjName.trim() || !currentOrg?.id) return;

    setIsCreatingProj(true);
    try {
      const p = await createProject(currentOrg.id, newProjName.trim());
      showToast({
        type: 'success',
        title: 'Project Created',
        message: `Project "${p.name}" created under ${currentOrg.name}.`,
      });
      setNewProjName('');
      onClose();
      navigate(`/organization/${currentOrg.id}/projects/${p.id}`);
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Failed to Create Project',
        message: err?.message || 'Server error',
      });
    } finally {
      setIsCreatingProj(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Create Project in ${currentOrg?.name || 'Organization'}`}
      maxWidth="sm"
    >
      <form onSubmit={handleCreateProject} className="space-y-4">
        <Input
          label="Project Name"
          type="text"
          size="xs"
          required
          value={newProjName}
          onChange={(e) => setNewProjName(e.target.value)}
          placeholder="e.g. Ingestion Webhooks"
        />

        <div className="flex justify-end gap-2 pt-2 border-t border-cf-border">
          <Button variant="secondary" size="xs" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="xs"
            type="submit"
            isLoading={isCreatingProj}
            disabled={isCreatingProj || !newProjName.trim()}
            leftIcon={<Plus size={13} />}
          >
            Create
          </Button>
        </div>
      </form>
    </Modal>
  );
};
